import {
  Box,
  Button,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  type ReceivedPublicBidDto,
  type SourcingEventV2Dto,
  type ViewDocumentAsOwnerDto,
  useListPublicBidsForEventQuery,
} from "../../../autogen/bff-api";
import { DocumentsAsOwnerTable } from "../../../common/documents/DocumentsAsOwnerTable";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { urls } from "../../../urls";
import { PublicBidStatusTag } from "./PublicBidStatusTag";

interface Props {
  event: SourcingEventV2Dto;
}

export const BidsTab = ({ event }: Props) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const eventId = event.id;
  const { data, isLoading, isError } = useListPublicBidsForEventQuery({ eventId });
  const bids = data?.bids ?? [];
  const documentsModal = useDisclosure();
  const [selectedBidId, setSelectedBidId] = useState<string | null>(null);
  const selectedBid = bids.find((bid) => bid.id === selectedBidId);
  const selectedDocuments = useMemo(
    () => (selectedBid ? getSubmittedBidDocuments(selectedBid) : []),
    [selectedBid],
  );
  const openDocuments = (bid: ReceivedPublicBidDto) => {
    setSelectedBidId(bid.id);
    documentsModal.onOpen();
  };
  const closeDocuments = () => {
    documentsModal.onClose();
    setSelectedBidId(null);
  };

  return (
    <Stack spacing="5">
      {isLoading ? (
        <Flex justify="center" py="16">
          <Spinner color="smPrimary" />
        </Flex>
      ) : isError ? (
        <Flex justify="center" py="16">
          <Text color="smError">{t("Could not load bids.")}</Text>
        </Flex>
      ) : bids.length === 0 ? (
        <Flex justify="center" py="16">
          <Text color="smMuted">{t("No bids delivered yet.")}</Text>
        </Flex>
      ) : (
        <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" overflow="hidden">
          <TableContainer>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>{t("Supplier")}</Th>
                  <Th>{t("Status")}</Th>
                  <Th>{t("Submitted")}</Th>
                  <Th>{t("Documents")}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {bids.map((bid) => {
                  const documents = getSubmittedBidDocuments(bid);
                  const detailUrl = urls.v2.sourcingEvents.bidDetail.go(eventId, bid.id);
                  return (
                    <Tr key={bid.id} _hover={{ bg: "smBackgroundSecondary" }}>
                      <Td fontWeight="medium" color="smText">
                        <Link
                          as={RouterLink}
                          to={detailUrl}
                          color="smText"
                          _hover={{ textDecoration: "underline", color: "smPrimary" }}
                        >
                          {bid.ownerName || t("Unknown")}
                        </Link>
                      </Td>
                      <Td>
                        <PublicBidStatusTag status={bid.status} size="sm" />
                      </Td>
                      <Td color="smMuted">{bid.submittedAt ? displayDate(bid.submittedAt) : "–"}</Td>
                      <Td>
                        {documents.length > 0 ? (
                          <Button size="sm" variant="outline" onClick={() => openDocuments(bid)}>
                            {t("View documents")} ({documents.length})
                          </Button>
                        ) : (
                          <Text color="smMuted" fontSize="sm">
                            {t("No documents")}
                          </Text>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}
      <Modal isOpen={documentsModal.isOpen} onClose={closeDocuments} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t("Submitted bid documents")}
            {selectedBid?.ownerName ? `: ${selectedBid.ownerName}` : ""}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="6">
            <DocumentsAsOwnerTable documents={selectedDocuments} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

const getSubmittedBidDocuments = (bid: ReceivedPublicBidDto): ViewDocumentAsOwnerDto[] =>
  [
    ...(bid.documents ?? []),
    ...((bid.qualificationResponses ?? []).flatMap((response) => response.evidenceDocuments ?? [])),
  ].filter((document, index, documents) => documents.findIndex((d) => d.id === document.id) === index);
